"use client";

const DB_NAME = "figmawyaDraftsDb";
const DB_VERSION = 1;
const STORE = "drafts";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is not available in this browser."));
      return;
    }

    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error("Failed to open IndexedDB."));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "draftId" });
        store.createIndex("by_userId", "userId", { unique: false });
        store.createIndex("by_type", "type", { unique: false });
        store.createIndex("by_updatedAt", "updatedAt", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

function withStore(mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const result = fn(store, tx);
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error || new Error("IndexedDB transaction failed."));
        tx.onabort = () => reject(tx.error || new Error("IndexedDB transaction aborted."));
      })
  );
}

export async function upsertDraft(draft) {
  if (!draft?.draftId) throw new Error("draftId is required");
  const now = Date.now();
  const next = {
    createdAt: draft.createdAt || now,
    updatedAt: now,
    ...draft,
  };
  await withStore("readwrite", (store) => store.put(next));
  return next;
}

export async function getDraft(draftId) {
  return withStore("readonly", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.get(draftId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error || new Error("Failed to read draft."));
    });
  });
}

export async function deleteDraft(draftId) {
  await withStore("readwrite", (store) => store.delete(draftId));
}

export async function listDrafts({ userId, type } = {}) {
  return withStore("readonly", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => {
        const all = Array.isArray(req.result) ? req.result : [];
        const filtered = all.filter((d) => {
          if (userId && d.userId !== userId) return false;
          if (type && d.type !== type) return false;
          return true;
        });
        filtered.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
        resolve(filtered);
      };
      req.onerror = () => reject(req.error || new Error("Failed to list drafts."));
    });
  });
}

